import { useEffect, useRef, useState, useCallback } from "react";

const LANES = [62, 152, 242];
const ROAD_H = 520;

function getRank(s) {
  if (s >= 50) return "S";
  if (s >= 30) return "A";
  if (s >= 15) return "B";
  if (s >= 5) return "C";
  return "D";
}

function getRankLabel(s) {
  if (s >= 50) return "ELITE OPERATIVE";
  if (s >= 30) return "FIELD ANALYST";
  if (s >= 15) return "CADET";
  if (s >= 5) return "INITIATE";
  return "TRAINEE";
}

const enemyColors = {
  red: { body: "#ef4444", top: "#991b1b" },
  purple: { body: "#a855f7", top: "#6b21a8" },
  amber: { body: "#f59e0b", top: "#92400e" },
};

let eidCounter = 0;

export default function CarGame() {
  const [playerLane, setPlayerLane] = useState(1);
  const [enemies, setEnemies] = useState([]);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [gameState, setGameState] = useState("idle");
  const [scorePops, setScorePops] = useState([]);
  const [crashFlash, setCrashFlash] = useState(false);

  const stateRef = useRef({ playerLane: 1, enemies: [], score: 0, speed: 1, gameRunning: false });
  const lastTimeRef = useRef(0);
  const spawnTimerRef = useRef(0);
  const frameRef = useRef(null);

  const triggerCrash = useCallback(() => {
    stateRef.current.gameRunning = false;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    setCrashFlash(true);
    setTimeout(() => setCrashFlash(false), 300);
    const finalScore = stateRef.current.score;
    setBest((prev) => {
      const newBest = Math.max(prev, finalScore);
      return newBest;
    });
    setEnemies([]);
    setGameState("over");
  }, []);

  const gameLoop = useCallback((ts) => {
    if (!stateRef.current.gameRunning) return;
    const dt = Math.min(ts - lastTimeRef.current, 50);
    lastTimeRef.current = ts;

    const baseSpd = 180 + (stateRef.current.speed - 1) * 60;
    const spawnInterval = Math.max(600, 1800 - (stateRef.current.speed - 1) * 150);

    let crashed = false;
    const px = LANES[stateRef.current.playerLane];
    const py = ROAD_H - 24 - 56;

    const updated = stateRef.current.enemies
      .map((e) => ({ ...e, y: e.y + baseSpd * dt / 1000 }))
      .filter((e) => {
        if (Math.abs(px - LANES[e.lane]) < 28 && Math.abs(py - e.y) < 48) {
          crashed = true;
        }
        return true;
      });

    if (crashed) { triggerCrash(); return; }

    const survived = [];
    let gained = 0;
    updated.forEach((e) => {
      if (e.y > ROAD_H + 10) {
        gained++;
        setScorePops((prev) => [
          ...prev,
          { id: Date.now() + Math.random(), x: LANES[e.lane] + 4, y: ROAD_H - 80 },
        ]);
        setTimeout(() => setScorePops((p) => p.slice(1)), 800);
      } else {
        survived.push(e);
      }
    });

    if (gained > 0) {
      const newScore = stateRef.current.score + gained;
      stateRef.current.score = newScore;
      const newSpeed = 1 + Math.floor(newScore / 5);
      stateRef.current.speed = newSpeed;
      setScore(newScore);
      setSpeed(newSpeed);
    }

    spawnTimerRef.current += dt;
    if (spawnTimerRef.current >= spawnInterval) {
      spawnTimerRef.current = 0;
      const colors = ["red", "purple", "amber"];
      const newE = {
        id: ++eidCounter,
        lane: Math.floor(Math.random() * 3),
        y: -60,
        color: colors[Math.floor(Math.random() * 3)],
      };
      survived.push(newE);
      if (stateRef.current.score > 10 && Math.random() > 0.5) {
        survived.push({
          id: ++eidCounter,
          lane: Math.floor(Math.random() * 3),
          y: -130,
          color: colors[Math.floor(Math.random() * 3)],
        });
      }
    }

    stateRef.current.enemies = survived;
    setEnemies([...survived]);
    frameRef.current = requestAnimationFrame(gameLoop);
  }, [triggerCrash]);

  const startGame = useCallback(() => {
    stateRef.current = { playerLane: 1, enemies: [], score: 0, speed: 1, gameRunning: true };
    spawnTimerRef.current = 0;
    setPlayerLane(1);
    setEnemies([]);
    setScore(0);
    setSpeed(1);
    setGameState("running");
    lastTimeRef.current = performance.now();
    setTimeout(() => {
      const e = { id: ++eidCounter, lane: Math.floor(Math.random() * 3), y: -60, color: "red" };
      stateRef.current.enemies = [e];
      setEnemies([e]);
    }, 400);
    frameRef.current = requestAnimationFrame(gameLoop);
  }, [gameLoop]);

  const movePlayer = useCallback((dir) => {
    const next = Math.max(0, Math.min(2, stateRef.current.playerLane + dir));
    stateRef.current.playerLane = next;
    setPlayerLane(next);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (gameState !== "running") return;
      if (e.key === "ArrowLeft" || e.key === "a") movePlayer(-1);
      if (e.key === "ArrowRight" || e.key === "d") movePlayer(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [gameState, movePlayer]);

  useEffect(() => () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); }, []);

  const rank = getRank(score);
  const col = enemyColors;

  return (
    <div
      className="min-h-screen bg-[#090D16] text-slate-200 flex flex-col items-center p-6"
      style={{
        backgroundImage: "radial-gradient(circle, rgba(6,182,212,0.06) 1.5px, transparent 1.5px)",
        backgroundSize: "28px 28px",
      }}
    >
      {/* Header */}
      <div className="w-full max-w-[440px] flex justify-between items-start mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[.3em] text-cyan-500 font-mono mb-0.5">White Room · Combat Drive</p>
          <h1 className="text-2xl font-bold tracking-widest font-mono text-cyan-400">
            ROAD <span className="text-slate-200 font-light">MATRIX</span>
          </h1>
        </div>
        <button
          onClick={startGame}
          className="px-4 py-2 border border-cyan-500/60 text-cyan-400 font-mono text-[11px] uppercase tracking-widest hover:bg-cyan-500/10 transition-all active:scale-95"
        >
          {gameState === "idle" ? "▶ INITIALIZE" : "↺ RESTART"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2.5 w-full max-w-[440px] mb-4">
        {[
          { label: "Score", value: score, color: "text-cyan-400" },
          { label: "Best", value: best, color: "text-slate-200" },
          { label: "Speed", value: `${speed}x`, color: "text-emerald-400" },
          { label: "Rank", value: rank, color: "text-cyan-400" },
        ].map((s, i) => (
          <div key={i} className="bg-slate-900/80 border border-slate-700/40 p-2.5 text-center">
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-0.5">{s.label}</p>
            <p className={`text-xl font-bold font-mono ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Road */}
      <div
        className={`relative bg-[#0f172a] border border-cyan-500/20 overflow-hidden mb-4 ${crashFlash ? "bg-red-900/20" : ""}`}
        style={{ width: 360, height: ROAD_H }}
      >
        {/* Road markings */}
        <div className="absolute top-0 left-[40px] w-[3px] h-full bg-slate-800" />
        <div className="absolute top-0 right-[40px] w-[3px] h-full bg-slate-800" />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-full"
          style={{ background: "repeating-linear-gradient(to bottom, #334155 0px, #334155 20px, transparent 20px, transparent 40px)" }}
        />
        <div
          className="absolute top-0 w-[1px] h-full opacity-25"
          style={{ left: "25%", background: "repeating-linear-gradient(to bottom, #334155 0px, #334155 15px, transparent 15px, transparent 35px)" }}
        />
        <div
          className="absolute top-0 w-[1px] h-full opacity-25"
          style={{ left: "75%", background: "repeating-linear-gradient(to bottom, #334155 0px, #334155 15px, transparent 15px, transparent 35px)" }}
        />

        {/* Score pops */}
        {scorePops.map((p) => (
          <div
            key={p.id}
            className="absolute font-bold text-cyan-400 font-mono text-sm pointer-events-none z-10 animate-bounce"
            style={{ left: p.x, top: p.y }}
          >
            +1
          </div>
        ))}

        {/* Player Car */}
        <div
          className="absolute flex flex-col items-center transition-all duration-100"
          style={{ bottom: 24, left: LANES[playerLane] }}
        >
          <div className="w-7 h-11 bg-cyan-400 rounded-t flex flex-col items-center justify-center relative">
            <div className="w-4 h-3.5 bg-cyan-700 rounded-sm mb-0.5" />
            <div className="w-1.5 h-1 bg-yellow-200 rounded-sm" />
            <div className="flex gap-1 mt-0.5">
              <div className="w-1 h-0.5 bg-red-400 rounded-sm" />
              <div className="w-1 h-0.5 bg-red-400 rounded-sm" />
            </div>
          </div>
          <div className="flex justify-between w-9">
            <div className="w-1.5 h-3 bg-slate-900 border border-slate-700 rounded-sm" />
            <div className="w-1.5 h-3 bg-slate-900 border border-slate-700 rounded-sm" />
          </div>
        </div>

        {/* Enemy Cars */}
        {enemies.map((e) => {
          const c = col[e.color];
          return (
            <div
              key={e.id}
              className="absolute flex flex-col items-center"
              style={{ left: LANES[e.lane], top: e.y }}
            >
              <div className="w-7 h-11 rounded-t flex flex-col items-center justify-center" style={{ background: c.body }}>
                <div className="w-4 h-3 rounded-sm mt-1" style={{ background: c.top }} />
              </div>
              <div className="flex justify-between w-9">
                <div className="w-1.5 h-3 bg-slate-900 border border-slate-700 rounded-sm" />
                <div className="w-1.5 h-3 bg-slate-900 border border-slate-700 rounded-sm" />
              </div>
            </div>
          );
        })}

        {/* Overlay — Idle */}
        {gameState === "idle" && (
          <div className="absolute inset-0 bg-[#090D16]/85 flex flex-col items-center justify-center gap-3 z-10">
            <p className="text-[10px] uppercase tracking-[.3em] text-cyan-500 font-mono">Neural Drive System</p>
            <p className="text-sm text-slate-400 font-mono tracking-widest">AVOID ENEMY VEHICLES</p>
            <p className="text-[11px] text-slate-600 font-mono text-center leading-loose">
              ARROW KEYS / A D<br />SPEED INCREASES EVERY 5 DODGES
            </p>
            <button
              onClick={startGame}
              className="mt-2 px-6 py-2.5 border border-cyan-500/60 text-cyan-400 font-mono text-[11px] uppercase tracking-widest hover:bg-cyan-500/10 active:scale-95"
            >
              ▶ START MISSION
            </button>
          </div>
        )}

        {/* Overlay — Game Over */}
        {gameState === "over" && (
          <div className="absolute inset-0 bg-[#090D16]/85 flex flex-col items-center justify-center gap-3 z-10">
            <p className="text-[11px] uppercase tracking-[.3em] text-rose-400 font-mono">Neural Drive Terminated</p>
            <h2 className="text-2xl font-bold tracking-widest text-rose-400 font-mono">GAME OVER</h2>
            <div className="text-4xl font-bold text-cyan-400 font-mono border border-cyan-500/30 px-8 py-2">
              {score}
            </div>
            <p className="text-[11px] uppercase tracking-widest text-emerald-400 font-mono">
              RANK {rank} — {getRankLabel(score)}
            </p>
            <p className="text-[10px] text-slate-500 font-mono">Best: {best} dodges</p>
            <button
              onClick={startGame}
              className="mt-2 px-6 py-2.5 border border-cyan-500/60 text-cyan-400 font-mono text-[11px] uppercase tracking-widest hover:bg-cyan-500/10 active:scale-95"
            >
              ↺ RESTART MISSION
            </button>
          </div>
        )}
      </div>

      {/* Touch Controls */}
      <div className="flex gap-3 mb-4">
        {[-1, 1].map((dir) => (
          <button
            key={dir}
            onPointerDown={() => movePlayer(dir)}
            className="w-12 h-10 border border-slate-700/50 bg-slate-900/80 text-slate-400 text-lg flex items-center justify-center active:bg-cyan-500/15 active:border-cyan-500 active:text-cyan-400 transition-all"
          >
            {dir === -1 ? "←" : "→"}
          </button>
        ))}
      </div>

      {/* Footer */}
      <div className="w-full max-w-[440px] flex justify-between items-center border-t border-slate-800/60 pt-3">
        <p className="text-[8px] font-mono uppercase tracking-widest text-slate-700">
          SEC-CLEARANCE: OMEGA · WR-DRIVE-09
        </p>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <p className="text-[8px] font-mono uppercase tracking-widest text-slate-700">TUNNEL STABLE</p>
        </div>
      </div>
    </div>
  );
}